import { Popover, TextInput } from "@narmi/design_system";
import React, { useState } from "react";
import { useCombobox } from "downshift";
import cc from "classcat";
import styles from "./SearchMenuButton.module.css";

/**
 * This is an NDS component waiting to be added to the library! It's just living here while we confirm the behavior in QA.
 * A SearchMenuButton is a popover whose content contains a list of clickable items that trigger side-effects (the "Menu"),
 * and an input field that allows the user to filter the list of items ("the Search"), stored in a Popover with a trigger (the "Button").
 *
 * This component does not have persist any state about the input value, items, or whether an item has been selected. That means the
 * consumer is responsible for defining the items, responding to changes to the input value, and handling what happens if an
 * item is selected.
 *
 * For example, the SavedReplySearch in the Messages ReplyBox uses this component to render a list of saved replies, which are
 * fetched from the server using the input as a search query. On click, a saved reply's body text is inserted into the reply box.
 */

export function SearchMenuButton<T>({
  trigger,
  inputLabel,
  onInputChange,
  onUserDismiss: onUserDismissExternal,
  items,
  itemToString,
  renderItem,
  onItemSelect,
  footerContent,
  side = "top",
}: {
  trigger: React.ReactNode;
  inputLabel: string;
  onInputChange: (value: string) => void;
  onUserDismiss: () => void;
  items: T[];
  itemToString: (item: T | null) => string;
  renderItem: (item: T, isHighlighted: boolean) => React.ReactNode;
  onItemSelect: (item: T) => void;
  footerContent?: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
}) {
  const [isOpen, setIsOpen] = useState(false);

  const handleDismiss = () => {
    reset();
    setIsOpen(false);
    onUserDismissExternal();
  };

  const { getInputProps, getMenuProps, getItemProps, highlightedIndex, closeMenu, reset } =
    useCombobox<T>({
      items,
      itemToString,
      onInputValueChange: ({ inputValue }) => {
        onInputChange(inputValue);
      },
      onSelectedItemChange: ({ selectedItem }) => {
        if (selectedItem) {
          onItemSelect(selectedItem);
          handleDismiss();
          closeMenu();
        }
      },
    });

  return (
    <Popover
      isOpen={isOpen}
      side={side}
      hasShadow={false}
      autoFocus
      onUserEnable={() => setIsOpen(true)}
      onUserDismiss={handleDismiss}
      content={
        <div className={styles.positionedElement}>
          <div className={`${styles.searchMenuButtonContents} rounded--all`}>
            <div className={styles.searchMenuButtonInput}>
              <TextInput label={inputLabel} {...getInputProps({}, { suppressRefError: true })} />
            </div>
            <ul
              className={`${styles.searchMenuButtonMenu} list--reset`}
              // downshift logs a console error if the popover content is not rendered,
              // as a means of suggesting that popover use display: none instead of unmounting its content on close.
              // we suppress that error here, since the NDS Popover behavior is out of this component's control.
              {...getMenuProps({}, { suppressRefError: true })}
            >
              {items.length === 0 ? (
                <li className={`${styles.searchMenuButtonItem} padding--x--s padding--y--xs`}>
                  No results found
                </li>
              ) : (
                items.map((item, index) => (
                  <li
                    key={itemToString(item)}
                    className={cc([
                      styles.searchMenuButtonItem,
                      "padding--x--s padding--y--xs",
                      {
                        [styles.searchMenuButtonItemHighlighted]: index === highlightedIndex,
                      },
                    ])}
                    {...getItemProps({ item, index })}
                  >
                    {renderItem(item, index === highlightedIndex)}
                  </li>
                ))
              )}
            </ul>
            {footerContent && (
              <div className={`${styles.searchMenuButtonFooter} padding--all--s border--top`}>
                {footerContent}
              </div>
            )}
          </div>
        </div>
      }
    >
      {trigger}
    </Popover>
  );
}
